"use client";

import { Badge } from "@/components/ui/badge";
import { useImport } from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { useToast } from "@/context/ToastContext";
import { useImportStatic } from "@/utils/hooks/import/importHooks";
import { useCountriesStatic } from "@/utils/hooks/static/staticHooks";
import { columnTypeErrors } from "@/utils/import/validations/columnValidations";
import { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { getOptionsComponent } from "./OptionsList";

// A country is copied to the clipboard when clicked, like the other option lists.
const CountriesList = ({ countries }: { countries: { value: number; label: string }[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { showToast } = useToast();

  if (countries.length === 0) return null;

  const copyCountry = async (name: string) => {
    try {
      await navigator.clipboard.writeText(name);
      showToast({ description: `"${name}" copiado al portapapeles`, variant: "success", duration: 2000 });
      setIsExpanded(false);
    } catch {
      showToast({ description: "No se pudo copiar al portapapeles", variant: "destructive", duration: 3000 });
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-800 hover:bg-yellow-200"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        Países disponibles
        {isExpanded ? <FiChevronUp className="h-3 w-3" /> : <FiChevronDown className="h-3 w-3" />}
      </button>
      {isExpanded && (
        <div className="absolute left-0 top-full z-10 mt-1 h-32 min-w-48 overflow-y-auto rounded border bg-white p-1 text-xs shadow-lg">
          {countries.map((country) => (
            <div
              key={country.value}
              className="cursor-pointer rounded p-1 text-gray-700 hover:bg-gray-100"
              onClick={() => copyCountry(country.label)}
              title={`Clic para copiar "${country.label}"`}
            >
              {country.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * What the file may carry: every field of the profile with whether it is required,
 * its format, an example and, for catalogs, the values that are accepted.
 */
export default function ImportColumnsReference() {
  const { importData } = useImport();
  const { selectedProfile } = importData;
  const { session } = useSession();
  const { languageCode } = session;
  const staticData = useImportStatic(languageCode);
  const { data: countries = [] } = useCountriesStatic(languageCode);
  const [isOpen, setIsOpen] = useState(false);

  const entries = Object.entries(selectedProfile.columns);

  return (
    <div className="rounded-md border border-bg+2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-3 text-left text-sm font-medium text-fg"
        aria-expanded={isOpen}
      >
        <span>Columnas que puede traer tu archivo ({entries.length})</span>
        {isOpen ? <FiChevronUp className="h-4 w-4" /> : <FiChevronDown className="h-4 w-4" />}
      </button>

      {isOpen && (
        <div className="max-h-80 overflow-auto border-t border-bg+2">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-bg+1 text-left text-xs text-fg-muted">
              <tr>
                <th className="p-2 font-medium">Columna</th>
                <th className="p-2 font-medium">Obligatoria</th>
                <th className="p-2 font-medium">Formato</th>
                <th className="p-2 font-medium">Ejemplo</th>
                <th className="p-2 font-medium">Valores</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, column]) => {
                const header =
                  column.fieldHeader?.[languageCode] || column.fieldHeader?.en || key;
                const format =
                  column.customError?.[languageCode] ||
                  column.customError?.en ||
                  columnTypeErrors[column.type]?.[languageCode] ||
                  "";
                const example = (column.sampleData?.[languageCode] || column.sampleData?.en || [])
                  .map((item) => String(item ?? ""))
                  .find((item) => item !== "");
                const lowerHeader = header.toLowerCase();
                const isCountry = column.type === "country";
                const options = isCountry
                  ? null
                  : getOptionsComponent(lowerHeader, staticData, undefined, true);

                return (
                  <tr key={key} className="border-t border-bg+2 align-top">
                    <td className="p-2 font-medium text-fg">{header}</td>
                    <td className="p-2">
                      {column.required ? (
                        <Badge tone="soft" color="destructive">
                          Sí
                        </Badge>
                      ) : (
                        <span className="text-fg-muted">No</span>
                      )}
                    </td>
                    <td className="p-2 text-xs text-fg-muted">{format}</td>
                    <td className="p-2 text-xs">{example ?? ""}</td>
                    <td className="p-2">
                      {isCountry ? <CountriesList countries={countries} /> : options}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
